import React, { CSSProperties, Component, useCallback, useContext, useEffect, useRef, useState } from 'react';
import GoogleMap from 'google-maps-react-markers'
import { AppContext } from '../context';
import Marker from './marker.component';
import mapOptions from '../json/map-options.json';
import useSupercluster from 'use-supercluster';
import { ObjectType } from '../types/object.type';
import '../styles/map.css';
import ObjectDataService from '../services/object.service';

function withHooks(WrappedComponent: typeof Map) {
  return function(props: any) {
    const context = useContext(AppContext);
    const mapRef = useRef<any>(null);
    const [initialMap, setInitialMap] = useState(true);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const [bounds, setBounds] = useState([0, 0, 0, 0]);
    const [zoom, setZoom] = useState(5);
    const [points, setPoints] = useState<Array<any>>([]);
    const [bubbleStyles, setBubbleStyles] = useState<CSSProperties>();
    const [mousePosition, setMousePosition] = useState();
    const { clusters, supercluster } = useSupercluster({
      points,
      bounds,
      zoom,
      options: { radius: 75, maxZoom: 20 }
    });

    const retrieveObjects = () => {
      const { state, dispatch } = context;
      if (state.status === "not-started") {
        state.status = "loading";
        dispatch({
          type: "setStatus",
          payload: {
            status: "loading"
          }
        });
        ObjectDataService.search(state.search)
          .then((response: any) => {
            state.results = response.data;
            dispatch({
              type: "setResults",
              payload: {
                results: response.data
              }
            });
            state.status = "success";
            dispatch({
              type: "setStatus",
              payload: {
                status: "success"
              }
            });
          })
          .catch((e: Error) => {
            state.status = "error";
            dispatch({
              type: "setStatus",
              payload: {
                status: "error"
              }
            });
          });
      }
    }

    const loadPoints = () => {
      const { state } = context;
      const results = (state.results && state.results.nb > 0 ? state.results.results : false);
      var newPoints: any[] = [];
      if (results) {
        newPoints = results.map(object => ({
          type: "Object",
          id: object.nid,
          properties: {
            cluster: false,
            object: object
          },
          geometry: {
            type: "Point",
            coordinates: [
              object.field_object_position_lng,
              object.field_object_position_lat
            ]
          }
        }));
      }
      if (points !== newPoints) {
        setPoints(newPoints);
      }
    }

    const autosizeMap = () => {
      if (initialMap) {
        setInitialMap(false);
        const { state } = context;
        if (! props.noUrl) {
          const queryParameters = new URLSearchParams(window.location.search || '');
          const centerParam = queryParameters.get("center");
          const zoomParam = queryParameters.get("zoom");
          if (centerParam) {
            const center = {
              lat: parseFloat(centerParam.split(',')[0]),
              lng: parseFloat(centerParam.split(',')[1])
            }
            mapRef.current.panTo(center);
            mapRef.current.setZoom((zoomParam ? parseFloat(zoomParam) : zoom));
            setZoom(mapRef.current.getZoom());

            const mapBounds = mapRef.current.getBounds();
            if (mapBounds) {
              const ne = mapBounds.getNorthEast();
              const sw = mapBounds.getSouthWest();
              setBounds([
                sw.lng(),
                sw.lat(),
                ne.lng(),
                ne.lat()
              ]);
            }
            return;
          }
        }
        if (! state.search || Object.keys(state.search).length === 0) {
          return;
        }
      }
      const { state } = context;
      const results = (state.results && state.results.nb > 0 ? state.results.results : false);
      if (! mapRef || ! mapRef.current || ! results || results.length === 0) {
        return;
      }
      var latLngBounds: any = {
        east: null,
        north: null,
        south: null,
        west: null
      };
      results.map((object: any) => {
        const longitude = object.field_object_position_lng;
        const latitude = object.field_object_position_lat;
        if (latLngBounds.east === null || latLngBounds.west === null || latLngBounds.north === null || latLngBounds.south === null) {
          latLngBounds = {
            east: parseFloat(longitude),
            north: parseFloat(latitude),
            south: parseFloat(latitude),
            west: parseFloat(longitude)
          }
          return null;
        }
        latLngBounds.east = parseFloat(latLngBounds.east < longitude ? longitude : latLngBounds.east);
        latLngBounds.west = parseFloat(latLngBounds.west > longitude ? longitude : latLngBounds.west);
        latLngBounds.north = parseFloat(latLngBounds.north < latitude ? latitude : latLngBounds.north);
        latLngBounds.south = parseFloat(latLngBounds.south > latitude ? latitude : latLngBounds.south);
        return null;
      });
      if (latLngBounds.east === null || latLngBounds.west === null || latLngBounds.north === null || latLngBounds.south === null) {
        return null;
      }
      mapRef.current.fitBounds(latLngBounds);
      setZoom(mapRef.current.getZoom());
      setBounds([
        latLngBounds.west,
        latLngBounds.south,
        latLngBounds.east,
        latLngBounds.north
      ]);
    }

    const autoHideObjects = useCallback(() => {
      const { stopHide, noUrl } = props;
      if (stopHide && stopHide === true && ! noUrl) {
        const searchParams = new URLSearchParams(window.location.search || '');
        searchParams.delete("center");
        searchParams.delete("zoom");
        window.history.replaceState({}, 'Searching', `${window.location.pathname}?${searchParams.toString()}`);
        return;
      }

      const { state, dispatch } = context;
      const results = (state.results && state.results.nb > 0 ? state.results.results : false);
      if (! results || results.length === 0) {
        return;
      }

      var somethingHasChanged = false;
      const objects = results.filter((object: ObjectType) => {
        const longitude = object.field_object_position_lng;
        const latitude = object.field_object_position_lat;
        if ( (longitude && bounds[0] > longitude)
          || (latitude && bounds[1] > latitude)
          || (longitude && bounds[2] < longitude)
          || (latitude && bounds[3] < latitude)
        ) {
          if (object.hidden === false) {
            somethingHasChanged = true;
          }
          object.hidden = true;
        } else {
          if (object.hidden === true) {
            somethingHasChanged = true;
          }
          object.hidden = false;
        }
        return object;
      });
      if (somethingHasChanged) {
        dispatch({
          type: "setResults",
          payload: {
            results: {
              nb: objects.length,
              results: objects
            }
          }
        });
      }
    }, [bounds, context, props]);

    useEffect(() => {
      autoHideObjects();
    }, [autoHideObjects]);

    return (
      <WrappedComponent
        mapRef={mapRef}
        mapReady={mapReady}
        setMapReady={setMapReady}
        clusters={clusters}
        supercluster={supercluster}
        bounds={bounds}
        setBounds={setBounds}
        zoom={zoom}
        setZoom={setZoom}
        points={points}
        setPoints={setPoints}
        selectedMarker={selectedMarker}
        setSelectedMarker={setSelectedMarker}
        bubbleStyles={bubbleStyles}
        setBubbleStyles={setBubbleStyles}
        mousePosition={mousePosition}
        setMousePosition={setMousePosition}
        loadPoints={loadPoints}
        autosizeMap={autosizeMap}
        autoHideObjects={autoHideObjects}
        retrieveObjects={retrieveObjects}
        initialMap={initialMap}
        {...props} />
    );
  };
}

class Map extends Component {
  static contextType = AppContext;
  context!: React.ContextType<typeof AppContext>;
  props: any;

  componentDidMount () {
    const { retrieveObjects, doSearch } = this.props;
    if (doSearch && doSearch === true) {
      retrieveObjects();
    }
  }

  onGoogleApiLoaded ({ map, maps }: any) {
    const { mapRef, setMapReady } = this.props;
    mapRef.current = map;
    setMapReady(true);
  }

  componentDidUpdate () {
    this.checkPoints();
  }

  onMapMouseDown (e: any) {
    const { setMousePosition } = this.props;
    setMousePosition({
      x: e.clientX,
      y: e.clientY
    })
  }

  onMapMouseUp (e: any) {
    const { selectedMarker, setSelectedMarker, mousePosition } = this.props;
    if (! mousePosition || selectedMarker === null) {
      return;
    }
    const delta = 6;
    if (Math.abs(e.clientX - mousePosition.x) < delta && Math.abs(e.clientY - mousePosition.y) < delta) {
      setSelectedMarker(null);
    }
  }

  onMarkerClick (e: any, { id, lat, lng }: any) {
    const { dispatch } = this.context;
    const { mapRef, clusters, supercluster, selectedMarker, setSelectedMarker, setBounds, setZoom, zoom } = this.props;
    if (selectedMarker && selectedMarker.id === id) {
      setSelectedMarker(null);
      dispatch({
        type: 'setHighlight',
        payload: {
          highlight: false
        }
      });
      return;
    }
    const cluster = clusters.find((cluster: any) => cluster.id === id);
    if (cluster.properties.cluster) {
      const expansionZoom = Math.min(
        supercluster.getClusterExpansionZoom(id),
        20
      );
      mapRef.current.setZoom(expansionZoom + 1);
      mapRef.current.panTo({lat: lat, lng: lng});
      setSelectedMarker(null);
      const bounds = mapRef.current.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      setZoom(mapRef.current.getZoom());
      setBounds([
        sw.lng(),
        sw.lat(),
        ne.lng(),
        ne.lat()
      ]);
      return;
    }

    setSelectedMarker(cluster);
    const { isMobile, setBubbleStyles } = this.props;
    const projection = mapRef.current.getProjection();
    const point = projection.fromLatLngToPoint({lat: parseFloat(lat), lng: parseFloat(lng)});
    const bounds = mapRef.current.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const pixelBounds = {
      ne: projection.fromLatLngToPoint({lat: parseFloat(ne.lat()), lng: parseFloat(ne.lng())}),
      sw: projection.fromLatLngToPoint({lat: parseFloat(sw.lat()), lng: parseFloat(sw.lng())})
    }
    const pixelSize = Math.pow(2, -zoom);
    var pointPosition = {
      top: (point.y - pixelBounds.ne.y)/pixelSize,
      bottom: (pixelBounds.sw.y - point.y)/pixelSize,
      left: (point.x - pixelBounds.sw.x)/pixelSize,
      right: (pixelBounds.ne.x - point.x)/pixelSize
    }

    var bubbleSize = {
      width: 250,
      height: 382,
      markerHeight: 61,
      markerWidth: 44,
      margin: 40
    };
    if (isMobile) {
      bubbleSize = {
        ...bubbleSize,
        width: 200,
        height: 294,
        margin: 15
      };
    }
    const availableDistances = {
      top: (bubbleSize.height + bubbleSize.markerHeight + (bubbleSize.margin * 2)) <= pointPosition.top,
      bottom: (bubbleSize.height + (bubbleSize.margin * 2)) <= pointPosition.bottom,
      right: (bubbleSize.width + (bubbleSize.markerWidth / 2) + (bubbleSize.margin * 2)) <= pointPosition.right,
      left: (bubbleSize.width + (bubbleSize.markerWidth / 2) + (bubbleSize.margin * 2)) <= pointPosition.left,
      center: {
        right: ((bubbleSize.width / 2) + bubbleSize.margin) <= pointPosition.right,
        left: ((bubbleSize.width / 2) + bubbleSize.margin) <= pointPosition.left,
        top: ((bubbleSize.height / 2) + bubbleSize.margin) <= pointPosition.top,
        bottom: ((bubbleSize.height / 2) + bubbleSize.margin) <= pointPosition.bottom
      }
    };

    /** Display on top of the marker */
    if (availableDistances.top) {
      if (availableDistances.center.right && availableDistances.center.left) {
        setBubbleStyles({
          bottom: "calc(100% + " + bubbleSize.margin + "px)",
          left: "50%",
          transform: "translate(-50%, 0)"
        });
        return;
      }
      if (! availableDistances.center.right) {
        const rightShift = ((bubbleSize.width / 2) + bubbleSize.margin) - pointPosition.right;
        setBubbleStyles({
          bottom: "calc(100% + " + bubbleSize.margin + "px)",
          left: "calc(50% - " + rightShift + "px)",
          transform: "translate(-50%, 0)"
        });
        return;
      }
      const leftShift = ((bubbleSize.width / 2) + bubbleSize.margin) - pointPosition.left;
      setBubbleStyles({
        bottom: "calc(100% + " + bubbleSize.margin + "px)",
        left: "calc(50% + " + leftShift + "px)",
        transform: "translate(-50%, 0)"
      });
      return;
    }

    /** Display under the marker */
    if (availableDistances.bottom) {
      if (availableDistances.center.right && availableDistances.center.left) {
        setBubbleStyles({
          top: "calc(100% + " + bubbleSize.margin + "px)",
          left: "50%",
          transform: "translate(-50%, 0)"
        });
        return;
      }
      if (! availableDistances.center.right) {
        const rightShift = ((bubbleSize.width / 2) + bubbleSize.margin) - pointPosition.right;
        setBubbleStyles({
          top: "calc(100% + " + bubbleSize.margin + "px)",
          left: "calc(50% - " + rightShift + "px)",
          transform: "translate(-50%, 0)"
        });
        return;
      }
      const leftShift = ((bubbleSize.width / 2) + bubbleSize.margin) - pointPosition.left;
      setBubbleStyles({
        top: "calc(100% + " + bubbleSize.margin + "px)",
        left: "calc(50% + " + leftShift + "px)",
        transform: "translate(-50%, 0)"
      });
      return;
    }

    /** Display to the right of the marker */
    if (availableDistances.right) {
      if (availableDistances.center.top && availableDistances.center.bottom) {
        setBubbleStyles({
          top: "50%",
          left: "calc(100% + " + bubbleSize.margin + "px)",
          transform: "translate(0, -50%)"
        });
        return;
      }
      if (! availableDistances.center.bottom) {
        const bottomShift = ((bubbleSize.height / 2) + bubbleSize.margin) - pointPosition.bottom;
        setBubbleStyles({
          top: "calc(50% - " + bottomShift + "px)",
          left: "calc(100% + " + bubbleSize.margin + "px)",
          transform: "translate(0, -50%)"
        });
        return;
      }
      const topShift = ((bubbleSize.height / 2) + bubbleSize.margin) - pointPosition.top;
      setBubbleStyles({
        top: "calc(50% + " + topShift + "px)",
        left: "calc(100% + " + bubbleSize.margin + "px)",
        transform: "translate(0, -50%)"
      });
      return;
    }

    /** Display to the left of the marker */
    if (availableDistances.left) {
      if (availableDistances.center.top && availableDistances.center.bottom) {
        setBubbleStyles({
          top: "50%",
          right: "calc(100% + " + bubbleSize.margin + "px)",
          transform: "translate(0, -50%)"
        });
        return;
      }
      if (! availableDistances.center.bottom) {
        const bottomShift = ((bubbleSize.height / 2) + bubbleSize.margin) - pointPosition.bottom;
        setBubbleStyles({
          top: "calc(50% - " + bottomShift + "px)",
          right: "calc(100% + " + bubbleSize.margin + "px)",
          transform: "translate(0, -50%)"
        });
        return;
      }
      const topShift = ((bubbleSize.height / 2) + bubbleSize.margin) - pointPosition.top;
      setBubbleStyles({
        top: "calc(50% + " + topShift + "px)",
        right: "calc(100% + " + bubbleSize.margin + "px)",
        transform: "translate(0, -50%)"
      });
      return;
    }

    /** Backup if te bubbles can’t be displayed in the viewport */
    setBubbleStyles({
      bottom: "calc(100% + " + bubbleSize.margin + "px)",
      left: "50%",
      transform: "translate(-50%, 0)"
    });
    const difference = 1 / Math.pow(2, (zoom - 8));
    const latitude = (parseFloat(lat) + difference);
    mapRef.current.panTo({
      lat: latitude,
      lng: parseFloat(lng)
    });
  }

  onMapChange ({ bounds, zoom }: any) {
    const { mapReady, setBounds, setZoom, initialMap, mapRef, noUrl } = this.props;
    if (!mapReady) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    setZoom(zoom);
    setBounds([
      sw.lng(),
      sw.lat(),
      ne.lng(),
      ne.lat()
    ]);

    if (! initialMap && ! noUrl) {
      const searchParams = new URLSearchParams(window.location.search || '');
      searchParams.set("center", mapRef.current.center.lat() + ',' + mapRef.current.center.lng());
      searchParams.set("zoom", zoom);
      window.history.replaceState({}, 'Searching', `${window.location.pathname}?${searchParams.toString()}`);
    }
  }

  checkPoints () {
    const { state } = this.context;
    const { points, mapReady, loadPoints, autosizeMap } = this.props;
    if (mapReady && state.results && state.results.results && points.length !== state.results.results.length) {
      loadPoints();
      autosizeMap();
    }
  }

  getGMapStatus () {
    return (window.google.maps ? 'ready' : 'loading');
  }

  render () {
    const defaultProps = {
      center: {
        lat: 46.4064240727434,
        lng: 6.720513326171869
      },
      zoom: 10
    };
    var selectedMarkerDisplayed = false;
    const { clusters, selectedMarker, zoom, height, isMobile, isTablet, bubbleStyles } = this.props;
    const [selectedLng, selectedLat] = (selectedMarker ? selectedMarker.geometry.coordinates : []);
    return (
      // Important! Always set the container height explicitly
      <div className={'map-container' + (isMobile ? ' mobile' : (isTablet ? ' tablet' : ''))} style={{ height: (height || 'min(100vh, 1270px)'), width: '100%' }}>
        {clusters &&
          <GoogleMap
            apiKey='AIzaSyCHTjzqRa47BfkkWvEfAtVHpPme3euLWVI'
            defaultCenter={defaultProps.center}
            defaultZoom={defaultProps.zoom}
            mapMinHeight={height || 'min(100vh, 1270px)'}
            onGoogleApiLoaded={this.onGoogleApiLoaded.bind(this)}
            onChange={this.onMapChange.bind(this)}
            loadScriptExternally={true}
            status={this.getGMapStatus()}
            options={mapOptions}
            events={[
              {
                name: 'onMouseDown',
                handler: this.onMapMouseDown.bind(this)
              },
              {
                name: 'onMouseUp',
                handler: this.onMapMouseUp.bind(this)
              }
            ]}
          >
            {clusters.map((cluster: any) => {
              const [longitude, latitude] = cluster.geometry.coordinates;
              const {
                cluster: isCluster,
                point_count: pointCount
              } = cluster.properties;

              if (isCluster) {
                return (
                  <Marker
                    key={`cluster-${cluster.id}`}
                    id={cluster.id}
                    text={pointCount}
                    lat={latitude}
                    lng={longitude}
                    zoom={zoom}
                    className='cluster'
                    selected={false}
                    zIndex={1}
                    onClick={(e) => this.onMarkerClick(e, {
                      id: cluster.id,
                      lat: latitude,
                      lng: longitude
                    })}
                  />
                );
              }

              if (selectedMarker && cluster.id === selectedMarker.id) {
                selectedMarkerDisplayed = true;
              }
              return (
                <Marker
                  key={`object-${cluster.properties.object.nid}`}
                  id={cluster.id}
                  text={''}
                  lat={latitude}
                  lng={longitude}
                  zoom={zoom}
                  object={cluster.properties.object}
                  className='object'
                  selected={selectedMarker && cluster.id === selectedMarker.id}
                  zIndex={selectedMarker && cluster.id === selectedMarker.id ? 2 : 1}
                  bubbleStyles={bubbleStyles}
                  onClick={(e) => this.onMarkerClick(e, {
                    id: cluster.id,
                    lat: latitude,
                    lng: longitude
                  })}
                />
              );
            })}
            {selectedMarker !== null && ! selectedMarkerDisplayed &&
              <Marker
                key={`object-${selectedMarker.properties.object.nid}`}
                id={selectedMarker.id}
                text={''}
                lat={parseFloat(selectedLat)}
                lng={parseFloat(selectedLng)}
                zoom={zoom}
                object={selectedMarker.properties.object}
                className='object'
                selected={true}
                zIndex={2}
                bubbleStyles={bubbleStyles}
                onClick={(e) => this.onMarkerClick(e, {
                  id: selectedMarker.id,
                  lat: selectedLat,
                  lng: selectedLng
                })}
              />}
          </GoogleMap>
        }
      </div>
    );
  }
}
export default withHooks(Map);
